import { estimateUserCountry, COUNTRY_US, COUNTRY_CA } from "./localisation";

import {
DATEFORMATS,
DATEFORMAT_EN,
DATEFORMAT_EN_US,
DATEFORMAT_ES_US,
DATEFORMAT_EN_CA,
ANSWERMODE_SUBMIT,
PRACTICEFORMAT_ISO,
PRACTICEFORMAT_LONGMONTH,
THEME_DARK,
THEME_LIGHT
} from './constants';

const STORAGE_KEY = "WEEKLE_SETTINGS";

const getSettings = () => {
  let dateFormat;
  let theme;
  let visits;
  let isMobileAlertClosed;
  let practiceFormat;
  let answerMode;
  try {
    const obj = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    dateFormat = obj.dateFormat;
    theme = obj.theme;
    visits = obj.visits;
    isMobileAlertClosed = obj.isMobileAlertClosed;
    practiceFormat = obj.practiceFormat;
    answerMode = obj.answerMode;
  } catch (ignore) {
    //console.error(ignore);
  }

  // todo: use language to get non-english defaults
  if (!DATEFORMATS.includes(dateFormat)) {
    switch (estimateUserCountry()) {
      case COUNTRY_US:
        dateFormat = DATEFORMAT_EN_US;
        break;
      case COUNTRY_CA:
        dateFormat = DATEFORMAT_EN_CA;
        break;
      default:
        dateFormat = DATEFORMAT_EN;
        break;
    }
  }

  const settings = {
    dateFormat,
    visits: 0,
    isMobileAlertClosed: !!isMobileAlertClosed,
    practiceFormat,
    answerMode
  };

  if (settings.answerMode !== ANSWERMODE_SUBMIT) {
    // empty is 'instant'
    delete settings.answerMode;
  }

  if (visits > 0 && visits % 1 === 0 && isFinite(visits)) {
    settings.visits = visits;
  }

  if (settings.practiceFormat !== PRACTICEFORMAT_ISO &&
     settings.practiceFormat !== PRACTICEFORMAT_LONGMONTH
  ) {
    delete settings.practiceFormat;
  }

  switch (theme) {
    case THEME_DARK:
    case THEME_LIGHT:
      settings.theme = theme;
      break;
    default:
  }

  return settings;
};

const saveSettings = (settings) => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
};

export const getIsMobileAlertClosed = () => {
  return getSettings().isMobileAlertClosed;
};

export const setIsMobileAlertClosed = () => {
  const value = true;
  const settings = getSettings();
  settings.isMobileAlertClosed = value;
  saveSettings(settings);

  triggerSettingsChangeListeners("isMobileAlertClosed", value);
};

export const getDateFormat = () => {
  return getSettings().dateFormat;
};

export const setDateFormat = (dateFormat) => {
  const settings = getSettings();
  settings.dateFormat = dateFormat;
  saveSettings(settings);

  triggerSettingsChangeListeners("dateFormat", dateFormat);
};

export const getAnswerMode = () => {
  return getSettings().answerMode;
};

export const setAnswerMode = (answerMode) => {
  const settings = getSettings();
  if (!answerMode) {
    delete settings.answerMode;
  } else {
    settings.answerMode = answerMode;
  }
  saveSettings(settings);

  triggerSettingsChangeListeners("answerMode", answerMode);
};

export const getPracticeFormat = () => {
  return getSettings().practiceFormat;
};

export const setPracticeFormat = (practiceFormat) => {
  const settings = getSettings();
  settings.practiceFormat = practiceFormat;
  saveSettings(settings);

  triggerSettingsChangeListeners("practiceFormat", practiceFormat);
};

export const getIsUsa = () => {
  const dateFormat = getDateFormat();
  return dateFormat === DATEFORMAT_EN_US || dateFormat === DATEFORMAT_ES_US;
};

export const getTheme = () => {
  return getSettings().theme;
};

export const getVisits = () => {
  return getSettings().visits;
};

export const incrementVisits = () => {
  const settings = getSettings();
  const visits = ++settings.visits;
  saveSettings(settings);

  triggerSettingsChangeListeners("visits", visits);
};

export const setTheme = (theme) => {
  const settings = getSettings();
  if (!theme) {
    delete settings.theme;
  } else {
    settings.theme = theme;
  }
  saveSettings(settings);

  triggerSettingsChangeListeners("theme", theme);
  triggerSettingsChangeListeners("isDark", getIsDarkTheme());
};

export const getIsDarkTheme = () => {
  const { theme } = getSettings();
  return theme ? theme === THEME_DARK : getDeviceDefaultIsDark();
};

export const getDeviceDefaultIsDark = () => {
  return !!(
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
};

if (window.matchMedia) {
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", (event) => {
      triggerSettingsChangeListeners("isDark", getIsDarkTheme());
    });
}

const _settingsChangeCallbacks = {};
export const addSettingsChangeListener = (key, callback) => {
  if (!_settingsChangeCallbacks[key]) {
    _settingsChangeCallbacks[key] = [];
  }
  _settingsChangeCallbacks[key].push(callback);
};

export const removeSettingsChangeListener = (key, callback) => {
  if (!_settingsChangeCallbacks[key]) {
    return;
  }
  const index = _settingsChangeCallbacks[key].lastIndexOf(callback);
  if (index >= 0) {
    _settingsChangeCallbacks[key].splice(index, 1);
  }
};

const triggerSettingsChangeListeners = (key, value) => {
  const arr = _settingsChangeCallbacks[key];
  if (!arr) {
    return;
  }
  for (let i = 0; i < arr.length; i++) {
    arr[i](value);
  }
};
